import { useEffect, useState } from 'react';
import {
  KNOWN_ORDER_STATUS_CODES,
  mergeNewestFirst,
  ORDER_STATUS_FILTERS,
  orderListParams,
  orderSearch,
  orderSearchField,
  orderStatusCodes,
  toOrderStatus,
} from '@mypoz/shared';
import type {
  OrderListQuery,
  OrderSearchField,
  OrderStatusFilter,
  SubmittedOrder,
} from '@mypoz/shared';

import { fetchOrderByNumber, fetchOrderPage } from '@/features/orders/api.js';

/** What one page of the Orders list resolved to. */
type PageAnswer =
  | { readonly kind: 'rows'; readonly rows: readonly unknown[]; readonly total: number }
  /** An order-number search: one detail record, or none. */
  | { readonly kind: 'detail'; readonly record: unknown };

export interface OrderListPage {
  readonly answer: PageAnswer | undefined;
  readonly counts: Readonly<Record<OrderStatusFilter, number>> | undefined;
  /** `loading` only before the first answer; later changes keep the last page, `refreshing`. */
  readonly status: 'loading' | 'ready' | 'error';
  readonly refreshing: boolean;
}

function emptyCounts(): Record<OrderStatusFilter, number> {
  const counts = {} as Record<OrderStatusFilter, number>;
  for (const filter of ORDER_STATUS_FILTERS) counts[filter] = 0;
  return counts;
}

/**
 * One page of orders for a status filter (MYP-126).
 *
 * `order_list` takes one status code per request (two answer 0 rows — checked live), so:
 * - all statuses: one request;
 * - a stage with one code: one request;
 * - a stage with several codes: each code's first `page × size` rows, merged newest first and cut
 *   to the page. The total is the codes' counts added up.
 */
async function loadPage(
  query: OrderListQuery,
  page: number,
  size: number,
  field: OrderSearchField | undefined,
): Promise<PageAnswer> {
  const search = orderSearch(query.search);
  if (search.kind === 'orderNumber') {
    return { kind: 'detail', record: await fetchOrderByNumber(search.orderNumber) };
  }

  const codes = orderStatusCodes(query.status);
  if (codes === undefined || codes.length <= 1) {
    const result = await fetchOrderPage(orderListParams(query, codes?.[0], page, size, field));
    return { kind: 'rows', rows: result.rows, total: result.count };
  }

  const perCode = await Promise.all(
    codes.map((code) => fetchOrderPage(orderListParams(query, code, 1, page * size, field))),
  );
  const merged = mergeNewestFirst(perCode.map((result) => result.rows));
  return {
    kind: 'rows',
    rows: merged.slice((page - 1) * size, page * size),
    total: perCode.reduce((sum, result) => sum + result.count, 0),
  };
}

/**
 * Every chip's count for the current search and channel: one `limit: 1` request per vendor status
 * code (only `count` is read), added up per stage, plus one for "All". These do not change with the
 * page or the chosen chip, so they are asked again only when the search or channel does.
 */
async function loadCounts(
  query: OrderListQuery,
  field: OrderSearchField | undefined,
): Promise<Record<OrderStatusFilter, number>> {
  const counts = emptyCounts();
  const [all, ...perCode] = await Promise.all([
    fetchOrderPage(orderListParams(query, undefined, 1, 1, field)),
    ...KNOWN_ORDER_STATUS_CODES.map((code) =>
      fetchOrderPage(orderListParams(query, code, 1, 1, field)),
    ),
  ]);
  counts.all = all?.count ?? 0;
  KNOWN_ORDER_STATUS_CODES.forEach((code, index) => {
    counts[toOrderStatus(code)] += perCode[index]?.count ?? 0;
  });
  return counts;
}

/** The shipping number on a summary row, for checking a shipping-number match. */
function shippingNumberOf(row: unknown): string {
  if (typeof row !== 'object' || row === null) return '';
  const value = (row as Record<string, unknown>)['airno'];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

/**
 * Which field a text search matches (MYP-127): asks the three the vendor can search — tracking
 * number, shipping number, receiver name — plus the unfiltered count, all at once, and lets
 * `orderSearchField` decide which answer to trust (the vendor answers a tracking or shipping
 * number that matches nothing with every order).
 */
async function resolveField(
  query: OrderListQuery,
  term: string,
): Promise<OrderSearchField | undefined> {
  const [base, tracking, shipping, name] = await Promise.all([
    fetchOrderPage(orderListParams({ ...query, search: '' }, undefined, 1, 1)),
    fetchOrderPage(orderListParams(query, undefined, 1, 1, 'tracking')),
    fetchOrderPage(orderListParams(query, undefined, 1, 50, 'shipping')),
    fetchOrderPage(orderListParams(query, undefined, 1, 1, 'name')),
  ]);
  return orderSearchField(term, {
    baseCount: base.count,
    trackingCount: tracking.count,
    shippingNumbers: shipping.rows.map(shippingNumberOf),
    nameCount: name.count,
  });
}

/**
 * The Orders list's current page and chip counts, asked of the vendor (MYP-126).
 *
 * An order-number search answers with one order, so its counts come from that order rather than
 * nineteen requests. Any other search first finds out which field it matches (`resolveField`).
 * `version` re-asks after the provider reloads (a new order, an edit).
 */
export function useOrderListPage(
  query: OrderListQuery,
  page: number,
  size: number,
  version: number,
  toOrderFromDetail: (record: unknown) => SubmittedOrder | undefined,
): OrderListPage & { retry: () => void } {
  const [answer, setAnswer] = useState<PageAnswer | undefined>(undefined);
  const [counts, setCounts] = useState<Record<OrderStatusFilter, number> | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const { search, status, channelId } = query;
  const parsed = orderSearch(search);
  const isOrderNumber = parsed.kind === 'orderNumber';
  const textTerm = parsed.kind === 'text' ? parsed.term : undefined;

  /*
    A text search first finds out which field it matches (MYP-127); the page and the counts wait
    for that. `resolved.key` ties an answer to the search and channel it was for, so a stale one
    is never used for a newer search.
  */
  const key = `${search}\u0000${channelId}\u0000${version}\u0000${attempt}`;
  const [resolved, setResolved] = useState<{ key: string; field: OrderSearchField | undefined }>();

  useEffect(() => {
    if (textTerm === undefined) return;
    let current = true;
    setRefreshing(true);

    resolveField({ search, status: 'all', channelId }, textTerm).then(
      (field) => {
        if (current) setResolved({ key, field });
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[orders] could not work out what the search matches', cause);
        setFailed(true);
        setRefreshing(false);
      },
    );

    return () => {
      current = false;
    };
  }, [key, search, channelId, textTerm]);

  const fieldReady = textTerm === undefined || resolved?.key === key;
  const field = textTerm === undefined ? undefined : resolved?.field;
  // A text search that matched none of the three fields: nothing to ask for.
  const noMatch = textTerm !== undefined && fieldReady && field === undefined;

  useEffect(() => {
    if (!fieldReady) return;
    let current = true;
    setRefreshing(true);
    setFailed(false);

    const request: Promise<PageAnswer> = noMatch
      ? Promise.resolve({ kind: 'rows', rows: [], total: 0 })
      : loadPage({ search, status, channelId }, page, size, field);

    request.then(
      (next) => {
        if (!current) return;
        setAnswer(next);
        setRefreshing(false);
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[orders] could not load a page of orders', cause);
        setFailed(true);
        setRefreshing(false);
      },
    );

    return () => {
      current = false;
    };
  }, [search, status, channelId, page, size, version, attempt, fieldReady, field, noMatch]);

  useEffect(() => {
    if (isOrderNumber || !fieldReady) return;
    let current = true;

    const request = noMatch
      ? Promise.resolve(emptyCounts())
      : loadCounts({ search, status: 'all', channelId }, field);

    request.then(
      (next) => {
        if (current) setCounts(next);
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[orders] could not count orders by status', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [search, channelId, isOrderNumber, version, attempt, fieldReady, field, noMatch]);

  // An order-number search: the one order (if any) is the whole answer, counted by its own stage.
  let shownCounts = counts;
  if (isOrderNumber && answer?.kind === 'detail') {
    shownCounts = emptyCounts();
    const order = answer.record === undefined ? undefined : toOrderFromDetail(answer.record);
    if (order && (channelId === 'all' || order.channelId === channelId)) {
      shownCounts.all = 1;
      shownCounts[order.status] = 1;
    }
  }

  const ready = answer !== undefined && shownCounts !== undefined;
  return {
    answer,
    counts: shownCounts,
    status: failed ? 'error' : ready ? 'ready' : 'loading',
    refreshing,
    retry: () => setAttempt((previous) => previous + 1),
  };
}
